import { Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PortCallModel } from '../../../../shared/models/port-call.model';
import { PortActivityTypes } from '../../../../shared/enums/port-activity-types.enum';
import { PortBerthingTypes } from '../../../../shared/enums/port-berthing-types.enum';
import { PortCallPdaStatuses } from '../../../../shared/enums/port-call-pda-status.enum';
import { TagContext } from '../../../../shared/enums/tag-context.enum';
import { DatePipe } from '@angular/common';
import { VoyageSummaryModel } from '../../../../shared/models/voyage-summary.model';
import { VoyageExpensesSegmentModel } from '../../../../shared/models/voyage-expenses-segment.model';
import { PortCallPdaModel } from '../../../../shared/models/port-call-pda.model';
import { VoyageOvertimeSummary } from '../voyage-overtime-summary/voyage-overtime-summary';

@Component({
  selector: 'app-voyage-details',
  imports: [TranslateModule, DatePipe, VoyageOvertimeSummary],
  templateUrl: './voyage-details.html',
})
export class VoyageDetails {
  private translateService = inject(TranslateService);

  //Inputs and outputs
  activeVoyage = input<VoyageSummaryModel | null>();
  activePortCall = input<PortCallModel | null>(null);
  activeExpenses = input<VoyageExpensesSegmentModel | null>(null);
  activePda = input<PortCallPdaModel | null>(null);
  setArrival = output<PortCallModel | null>();
  setDeparture = output<PortCallModel | null>();
  viewPdaBreakdown = output<void>();

  //Models
  portActivityTypes = PortActivityTypes;
  portBerthingTypes = PortBerthingTypes;

  //Computed
  stayHours = computed(() => {
    const pda = this.activePda();
    if (pda?.stayHours) return Number(pda.stayHours);
    return this.computeStayHoursFromDates();
  });

  stayDays = computed(() => {
    const pda = this.activePda();
    if (pda?.stayDays) return Number(pda.stayDays);
    const hours = this.computeStayHoursFromDates();
    return hours != null ? hours / 24 : null;
  });

  appliedBerthingType = computed(() => {
    return this.activePortCall()?.plannedBerthingType ?? this.activePda()?.port?.berthingType ?? null;
  });

  // Falls back to a humanized raw value when the enum/translation catalog hasn't caught up with
  // a value the API already returns (e.g. a berthing type not yet in PortBerthingTypes).
  appliedBerthingTypeLabel = computed(() => {
    const value = this.appliedBerthingType();
    if (!value) return null;

    const key = `portBerthingTypes.${value}`;
    const translated = this.translateService.instant(key);
    return translated === key ? this.humanize(value) : translated;
  });

  pdaStatusContext = computed<TagContext>(() => {
    switch (this.activePda()?.status) {
      case PortCallPdaStatuses.CALCULATED:
        return TagContext.SUCCESS;
      case PortCallPdaStatuses.MISSING_DATA:
        return TagContext.WARNING;
      case PortCallPdaStatuses.APPROVED:
        return TagContext.CURRENT;
      default:
        return TagContext.DEFAULT;
    }
  });

  //Events
  onArrival = () => {
    this.setArrival.emit(this.activePortCall());
  };

  onDeparture = () => {
    this.setDeparture.emit(this.activePortCall());
  };

  onViewPdaBreakdown(): void {
    this.viewPdaBreakdown.emit();
  }

  private humanize(value: string): string {
    return value
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private computeStayHoursFromDates(): number | null {
    const portCall = this.activePortCall();
    const arrival = portCall?.actualArrival ?? portCall?.estimatedArrival;
    const departure = portCall?.actualDeparture ?? portCall?.estimatedDeparture;
    if (!arrival || !departure) return null;

    const diffMs = new Date(departure).getTime() - new Date(arrival).getTime();
    return diffMs > 0 ? diffMs / (1000 * 60 * 60) : null;
  }
}
